import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Navbar, Container, NavDropdown } from 'react-bootstrap';
import Icon from '../common/Icons';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { getRoleLabel } from '../../constants/roles';
import { PLATFORM_BRANDING } from '../../constants/branding';
import { useNotificationSummary } from '../../hooks/useNotificationSummary';
import { ReceptionToastStack } from '../receptionist/ui';
import { AP_COPY } from './adminPersonnelCopy';
import { AP_PATHS, AP_NAV_ITEMS } from './adminPersonnelPaths';
import '../receptionist/ReceptionistDashboard.css';
import './AdminPersonnelPortal.css';

const SIDEBAR_STORAGE_KEY = 'ap-sidebar-collapsed';

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function AdminPersonnelNavMenu({ items, unreadNotifications, onNavigate, collapsed = false }) {
  return (
    <div className="ap-menu-section">
      <div className="ap-menu-section__items">
        {items.map(({ to, end, label, hint, icon, showUnreadBadge }) => (
          <NavLink
            key={to}
            to={to}
            end={!!end}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={({ isActive }) => `ap-menu-item${isActive ? ' is-active' : ''}`}
          >
            <span className="ap-menu-item__icon" aria-hidden>
              <Icon name={icon} size={18} />
            </span>
            <span className="ap-menu-item__text">
              <span className="ap-menu-item__row">
                <span className="ap-menu-item__label">{label}</span>
                {showUnreadBadge && unreadNotifications > 0 && !collapsed ? (
                  <span className="ap-menu-item__badge" aria-hidden>
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                ) : null}
              </span>
              {hint ? <span className="ap-menu-item__hint">{hint}</span> : null}
            </span>
            {showUnreadBadge && unreadNotifications > 0 && collapsed ? (
              <span className="ap-menu-item__badge ap-menu-item__badge--dot" aria-hidden />
            ) : null}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default function AdminPersonnelLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { ACCENT } = PLATFORM_BRANDING.NAME_PARTS;
  const { toasts, showToast, removeToast, getDuration } = useToast();

  const toast = useCallback((variant, msg) => {
    showToast(msg, variant);
  }, [showToast]);

  const outletValue = useMemo(() => ({ toast, showToast }), [toast, showToast]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const initials =
    `${user?.last_name?.charAt(0) || ''}${user?.first_name?.charAt(0) || ''}`.toUpperCase() || 'P';

  const navItems = useMemo(() => AP_NAV_ITEMS, []);
  const { unread: unreadNotifications } = useNotificationSummary();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMobileNav();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileNavOpen, closeMobileNav]);

  const layoutClassName = [
    'admin-personnel-portal',
    'ap-layout',
    'admin-layout',
    'receptionist-dashboard',
    'receptionist-layout',
    'min-vh-100',
    'd-flex',
    'flex-column',
    sidebarCollapsed ? 'ap-sidebar-is-collapsed' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const userMenu = (
    <NavDropdown
      align="end"
      className="ap-user-menu"
      id="ap-user-menu"
      title={
        <span className="ap-user-menu__trigger" aria-label={AP_COPY.ARIA_USER_MENU}>
          <span className="ap-user-menu__avatar" aria-hidden>
            {initials}
          </span>
          <span className="ap-user-menu__meta d-none d-md-flex">
            <span className="ap-user-menu__name">
              {user?.last_name}
              {user?.first_name ? ` ${user?.first_name}` : ''}
            </span>
            <span className="ap-user-menu__role">{getRoleLabel(user?.role)}</span>
          </span>
          <Icon name="chevronDown" size={14} className="ap-user-menu__chevron d-none d-md-block" aria-hidden />
        </span>
      }
    >
      <NavDropdown.Header className="ap-user-menu__header">
        <span className="ap-user-menu__header-avatar" aria-hidden>
          {initials}
        </span>
        <span className="ap-user-menu__header-text">
          <span className="ap-user-menu__header-name">
            {user?.last_name}
            {user?.first_name ? ` ${user?.first_name}` : ''}
          </span>
          {user?.email ? <span className="ap-user-menu__header-email">{user.email}</span> : null}
          <span className="ap-user-menu__role-badge">{getRoleLabel(user?.role)}</span>
        </span>
      </NavDropdown.Header>
      <NavDropdown.Divider className="ap-user-menu__divider" />
      <NavDropdown.Item className="ap-user-menu__action" onClick={() => navigate(AP_PATHS.PROFILE)}>
        Mon profil
      </NavDropdown.Item>
      <NavDropdown.Item className="ap-user-menu__action" onClick={() => navigate(AP_PATHS.SETTINGS)}>
        Paramètres
      </NavDropdown.Item>
      <NavDropdown.Divider className="ap-user-menu__divider" />
      <NavDropdown.Item
        className="ap-user-menu__action ap-user-menu__action--danger"
        onClick={handleLogout}
      >
        <Icon name="signOut" size={16} aria-hidden />
        Déconnexion
      </NavDropdown.Item>
    </NavDropdown>
  );

  return (
    <div className={layoutClassName}>
      <Navbar variant="dark" expand="lg" className="ap-topbar flex-shrink-0 recv-no-print shadow-sm">
        <Container fluid className="ap-topbar__container">
          <Navbar.Brand
            className="ap-topbar__brand-wrap mb-0"
            onClick={() => {
              closeMobileNav();
              navigate(AP_PATHS.DASHBOARD);
            }}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={PLATFORM_BRANDING.LOGO_WORDMARK_SRC}
              alt={PLATFORM_BRANDING.LOGO_WORDMARK_ALT}
              className="ap-topbar__wordmark"
            />
            <span className="ap-topbar__brand">
              <span className="ap-topbar__brand-product">{ACCENT}</span>
              <span className="ap-topbar__brand-section">{AP_COPY.LAYOUT.NAVBAR_BRAND}</span>
            </span>
          </Navbar.Brand>

          <div className="ap-topbar__toolbar ms-auto d-flex align-items-center gap-2">
            <NavLink
              to={AP_PATHS.NOTIFICATIONS}
              className={({ isActive }) => `ap-topbar__notif-btn ${isActive ? 'is-active' : ''}`}
              title="Notifications"
              aria-label={
                unreadNotifications > 0
                  ? `Notifications, ${unreadNotifications} non lue(s)`
                  : 'Notifications'
              }
            >
              <Icon name="bell" size={18} />
              {unreadNotifications > 0 && (
                <span className="ap-topbar__notif-badge" aria-hidden>
                  {unreadNotifications > 99 ? '99+' : unreadNotifications}
                </span>
              )}
            </NavLink>
            {userMenu}
            <button
              type="button"
              className={`ap-topbar__toggle d-lg-none navbar-toggler ${
                mobileNavOpen ? 'is-open' : ''
              }`}
              aria-controls="ap-mobile-drawer"
              aria-expanded={mobileNavOpen}
              aria-label={mobileNavOpen ? AP_COPY.ARIA_CLOSE_MENU : AP_COPY.ARIA_TOGGLE_MENU}
              onClick={() => (mobileNavOpen ? closeMobileNav() : openMobileNav())}
            >
              {mobileNavOpen ? (
                <Icon name="times" size={20} className="ap-topbar__toggle-icon" aria-hidden />
              ) : (
                <span className="navbar-toggler-icon" />
              )}
            </button>
          </div>
        </Container>
      </Navbar>

      {mobileNavOpen ? (
        <>
          <button
            type="button"
            className="ap-mobile-drawer__backdrop recv-no-print"
            aria-label={AP_COPY.ARIA_CLOSE_MENU}
            onClick={closeMobileNav}
          />
          <aside
            id="ap-mobile-drawer"
            className="ap-mobile-drawer recv-no-print"
            role="dialog"
            aria-modal="true"
            aria-label={AP_COPY.MOBILE_NAV_TITLE}
          >
            <div className="ap-mobile-drawer__head">
              <span className="ap-mobile-drawer__title">{AP_COPY.MOBILE_NAV_TITLE}</span>
              <button
                type="button"
                className="ap-mobile-drawer__close"
                aria-label={AP_COPY.ARIA_CLOSE_MENU}
                onClick={closeMobileNav}
              >
                <Icon name="times" size={18} />
              </button>
            </div>
            <nav className="ap-sidebar__nav ap-mobile-drawer__nav" aria-label="Navigation principale">
              <AdminPersonnelNavMenu
                items={navItems}
                unreadNotifications={unreadNotifications}
                onNavigate={closeMobileNav}
              />
            </nav>
          </aside>
        </>
      ) : null}

      <div className="d-flex flex-grow-1 overflow-hidden recv-workspace">
        <aside
          className={`recv-sidebar ap-sidebar d-none d-lg-flex flex-column flex-shrink-0 recv-no-print${
            sidebarCollapsed ? ' is-collapsed' : ''
          }`}
          aria-label={AP_COPY.LAYOUT.SIDE_MENU_SECTION}
        >
          <nav className="ap-sidebar__nav" aria-label="Navigation principale">
            <AdminPersonnelNavMenu
              items={navItems}
              unreadNotifications={unreadNotifications}
              collapsed={sidebarCollapsed}
            />
          </nav>
          <div className="ap-sidebar__footer">
            <button
              type="button"
              className="ap-sidebar__toggle"
              onClick={toggleSidebar}
              aria-expanded={!sidebarCollapsed}
              aria-label={
                sidebarCollapsed ? AP_COPY.ARIA_EXPAND_SIDEBAR : AP_COPY.ARIA_TOGGLE_SIDEBAR
              }
            >
              <Icon
                name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'}
                size={18}
                aria-hidden
              />
              <span className="ap-sidebar__toggle-label">
                {sidebarCollapsed ? AP_COPY.SIDEBAR_EXPAND : AP_COPY.SIDEBAR_COLLAPSE}
              </span>
            </button>
          </div>
        </aside>

        <main className="recv-main flex-grow-1 overflow-auto">
          <Container fluid className="py-4 px-md-4 recv-dash-container ap-dash-container">
            <Outlet context={outletValue} />
          </Container>
        </main>
      </div>

      <ReceptionToastStack toasts={toasts} onRemove={removeToast} getDuration={getDuration} />
    </div>
  );
}
